/**
 * Reports Page
 * Financial and business reports
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  Badge,
} from '../../components/ui/index.js';
import { reportsService } from '../../services/reports.js';
import type {
  ReportGroupBy,
  ExpenseGroupBy,
  SalesReportEntry,
  ExpenseReportEntry,
  CustomerReportEntry,
  ProductSalesEntry,
  AgedReceivablesEntry,
} from '../../types/reports.js';

type ReportType = 'sales' | 'income' | 'expenses' | 'customers' | 'products' | 'receivables';

export function ReportsPage() {
  const { t } = useTranslation();

  const [activeReport, setActiveReport] = useState<ReportType>('sales');
  const [dateRange, setDateRange] = useState(() => {
    const end = new Date();
    const start = new Date();
    start.setMonth(start.getMonth() - 1);
    return {
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0],
    };
  });
  const [groupBy, setGroupBy] = useState<ReportGroupBy>('day');
  const [expenseGroupBy, setExpenseGroupBy] = useState<ExpenseGroupBy>('category');

  // Sales Report
  const { data: salesData, isLoading: salesLoading } = useQuery({
    queryKey: ['report-sales', dateRange, groupBy],
    queryFn: () =>
      reportsService.getSalesReport({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        groupBy,
      }),
    enabled: activeReport === 'sales',
  });

  // Income Statement
  const { data: incomeData, isLoading: incomeLoading } = useQuery({
    queryKey: ['report-income', dateRange],
    queryFn: () =>
      reportsService.getIncomeStatement({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        compareWithPrevious: true,
      }),
    enabled: activeReport === 'income',
  });

  // Expense Report
  const { data: expenseData, isLoading: expenseLoading } = useQuery({
    queryKey: ['report-expenses', dateRange, expenseGroupBy],
    queryFn: () =>
      reportsService.getExpenseReport({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        groupBy: expenseGroupBy,
      }),
    enabled: activeReport === 'expenses',
  });

  // Customer Report
  const { data: customerData, isLoading: customerLoading } = useQuery({
    queryKey: ['report-customers', dateRange],
    queryFn: () =>
      reportsService.getCustomerReport({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        sortBy: 'total_purchases',
        order: 'desc',
        limit: 50,
      }),
    enabled: activeReport === 'customers',
  });

  // Product Sales Report
  const { data: productData, isLoading: productLoading } = useQuery({
    queryKey: ['report-products', dateRange],
    queryFn: () =>
      reportsService.getProductSalesReport({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        sortBy: 'revenue',
        order: 'desc',
        limit: 50,
      }),
    enabled: activeReport === 'products',
  });

  // Aged Receivables
  const { data: receivablesData, isLoading: receivablesLoading } = useQuery({
    queryKey: ['report-receivables'],
    queryFn: () => reportsService.getAgedReceivables(),
    enabled: activeReport === 'receivables',
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatPercent = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  const reports: { key: ReportType; label: string; icon: React.ReactNode }[] = [
    {
      key: 'sales',
      label: 'Sales Report',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
    },
    {
      key: 'income',
      label: 'Income Statement',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    {
      key: 'expenses',
      label: 'Expense Report',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
    },
    {
      key: 'customers',
      label: 'Customer Report',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
    },
    {
      key: 'products',
      label: 'Product Sales',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
        </svg>
      ),
    },
    {
      key: 'receivables',
      label: 'Aged Receivables',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">{t('reports.title', 'Reports')}</h1>
      </div>

      {/* Report Type Selection */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {reports.map((report) => (
          <button
            key={report.key}
            onClick={() => setActiveReport(report.key)}
            className={`p-4 rounded-lg border-2 transition-colors ${
              activeReport === report.key
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className={`flex flex-col items-center gap-2 ${activeReport === report.key ? 'text-blue-600' : 'text-gray-600'}`}>
              {report.icon}
              <span className="text-sm font-medium">{report.label}</span>
            </div>
          </button>
        ))}
      </div>

      {/* Date Range Filter */}
      {activeReport !== 'receivables' && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-wrap items-center gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <input
                  type="date"
                  value={dateRange.startDate}
                  onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
                  className="rounded-md border border-gray-300 px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                <input
                  type="date"
                  value={dateRange.endDate}
                  onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
                  className="rounded-md border border-gray-300 px-3 py-2"
                />
              </div>
              {activeReport === 'sales' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Group By</label>
                  <select
                    value={groupBy}
                    onChange={(e) => setGroupBy(e.target.value as ReportGroupBy)}
                    className="rounded-md border border-gray-300 px-3 py-2"
                  >
                    <option value="day">Day</option>
                    <option value="week">Week</option>
                    <option value="month">Month</option>
                    <option value="quarter">Quarter</option>
                    <option value="year">Year</option>
                  </select>
                </div>
              )}
              {activeReport === 'expenses' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Group By</label>
                  <select
                    value={expenseGroupBy}
                    onChange={(e) => setExpenseGroupBy(e.target.value as ExpenseGroupBy)}
                    className="rounded-md border border-gray-300 px-3 py-2"
                  >
                    <option value="category">Category</option>
                    <option value="vendor">Vendor</option>
                    <option value="month">Month</option>
                  </select>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sales Report */}
      {activeReport === 'sales' && (
        <Card>
          <CardHeader>
            <CardTitle>Sales Report</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Period</TableHead>
                  <TableHead className="text-right">Invoices</TableHead>
                  <TableHead className="text-right">Total Sales</TableHead>
                  <TableHead className="text-right">Tax</TableHead>
                  <TableHead className="text-right">Discounts</TableHead>
                  <TableHead className="text-right">Net Sales</TableHead>
                  <TableHead className="text-right">Paid</TableHead>
                  <TableHead className="text-right">Outstanding</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {salesLoading ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-8 text-center text-sm text-gray-500">Loading...</td>
                  </tr>
                ) : !salesData?.length ? (
                  <TableEmpty colSpan={8} message="No sales data for this period" />
                ) : (
                  <>
                    {salesData.map((row: SalesReportEntry, idx: number) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">{row.period}</TableCell>
                        <TableCell className="text-right">{row.invoiceCount}</TableCell>
                        <TableCell className="text-right">{formatCurrency(row.totalSales)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(row.totalTax)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(row.totalDiscount)}</TableCell>
                        <TableCell className="text-right font-medium">{formatCurrency(row.netSales)}</TableCell>
                        <TableCell className="text-right text-green-600">{formatCurrency(row.paidAmount)}</TableCell>
                        <TableCell className="text-right text-orange-600">{formatCurrency(row.outstandingAmount)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="bg-gray-50 font-semibold">
                      <TableCell>Total</TableCell>
                      <TableCell className="text-right">{salesData.reduce((sum, r) => sum + r.invoiceCount, 0)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(salesData.reduce((sum, r) => sum + r.totalSales, 0))}</TableCell>
                      <TableCell className="text-right">{formatCurrency(salesData.reduce((sum, r) => sum + r.totalTax, 0))}</TableCell>
                      <TableCell className="text-right">{formatCurrency(salesData.reduce((sum, r) => sum + r.totalDiscount, 0))}</TableCell>
                      <TableCell className="text-right">{formatCurrency(salesData.reduce((sum, r) => sum + r.netSales, 0))}</TableCell>
                      <TableCell className="text-right text-green-600">{formatCurrency(salesData.reduce((sum, r) => sum + r.paidAmount, 0))}</TableCell>
                      <TableCell className="text-right text-orange-600">{formatCurrency(salesData.reduce((sum, r) => sum + r.outstandingAmount, 0))}</TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Income Statement */}
      {activeReport === 'income' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Revenue</CardTitle>
            </CardHeader>
            <CardContent>
              {incomeLoading ? (
                <div className="py-8 text-center text-gray-500">Loading...</div>
              ) : !incomeData ? (
                <div className="py-8 text-center text-gray-500">No data available</div>
              ) : (
                <div className="space-y-4">
                  <div className="flex justify-between py-2 border-b">
                    <span>Invoiced Sales</span>
                    <span className="font-medium">{formatCurrency(incomeData.revenue.invoicedSales)}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span>Paid Sales</span>
                    <span className="font-medium">{formatCurrency(incomeData.revenue.paidSales)}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span>Other Income</span>
                    <span className="font-medium">{formatCurrency(incomeData.revenue.otherIncome)}</span>
                  </div>
                  <div className="flex justify-between py-2 text-lg font-semibold text-green-600">
                    <span>Total Revenue</span>
                    <span>{formatCurrency(incomeData.revenue.totalRevenue)}</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Expenses by Category</CardTitle>
            </CardHeader>
            <CardContent>
              {incomeLoading ? (
                <div className="py-8 text-center text-gray-500">Loading...</div>
              ) : !incomeData ? (
                <div className="py-8 text-center text-gray-500">No data available</div>
              ) : (
                <div className="space-y-4">
                  {incomeData.expenses.byCategory.map((cat, idx) => (
                    <div key={idx} className="flex justify-between py-2 border-b">
                      <span>{cat.category}</span>
                      <span className="font-medium">{formatCurrency(cat.amount)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between py-2 text-lg font-semibold text-red-600">
                    <span>Total Expenses</span>
                    <span>{formatCurrency(incomeData.expenses.totalExpenses)}</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>Net Income Summary</CardTitle>
            </CardHeader>
            <CardContent>
              {incomeLoading ? (
                <div className="py-8 text-center text-gray-500">Loading...</div>
              ) : !incomeData ? (
                <div className="py-8 text-center text-gray-500">No data available</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="p-4 bg-green-50 rounded-lg">
                    <p className="text-sm text-green-600">Total Revenue</p>
                    <p className="text-2xl font-bold text-green-700">{formatCurrency(incomeData.revenue.totalRevenue)}</p>
                  </div>
                  <div className="p-4 bg-red-50 rounded-lg">
                    <p className="text-sm text-red-600">Total Expenses</p>
                    <p className="text-2xl font-bold text-red-700">{formatCurrency(incomeData.expenses.totalExpenses)}</p>
                  </div>
                  <div className={`p-4 rounded-lg ${incomeData.netIncome >= 0 ? 'bg-blue-50' : 'bg-orange-50'}`}>
                    <p className={`text-sm ${incomeData.netIncome >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>Net Income</p>
                    <p className={`text-2xl font-bold ${incomeData.netIncome >= 0 ? 'text-blue-700' : 'text-orange-700'}`}>
                      {formatCurrency(incomeData.netIncome)}
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Expense Report */}
      {activeReport === 'expenses' && (
        <Card>
          <CardHeader>
            <CardTitle>Expense Report by {expenseGroupBy === 'category' ? 'Category' : expenseGroupBy === 'vendor' ? 'Vendor' : 'Month'}</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{expenseGroupBy === 'category' ? 'Category' : expenseGroupBy === 'vendor' ? 'Vendor' : 'Month'}</TableHead>
                  <TableHead className="text-right">Count</TableHead>
                  <TableHead className="text-right">Total Amount</TableHead>
                  <TableHead className="text-right">Tax Amount</TableHead>
                  <TableHead className="text-right">% of Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {expenseLoading ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-8 text-center text-sm text-gray-500">Loading...</td>
                  </tr>
                ) : !expenseData?.length ? (
                  <TableEmpty colSpan={5} message="No expense data for this period" />
                ) : (
                  expenseData.map((row: ExpenseReportEntry, idx: number) => (
                    <TableRow key={idx}>
                      <TableCell className="font-medium">{row.groupLabel}</TableCell>
                      <TableCell className="text-right">{row.expenseCount}</TableCell>
                      <TableCell className="text-right">{formatCurrency(row.totalAmount)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(row.taxAmount)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <div className="w-16 bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-blue-600 h-2 rounded-full"
                              style={{ width: `${Math.min(row.percentage, 100)}%` }}
                            />
                          </div>
                          <span className="text-sm">{formatPercent(row.percentage)}</span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Customer Report */}
      {activeReport === 'customers' && (
        <Card>
          <CardHeader>
            <CardTitle>Top Customers</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Customer</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead className="text-right">Invoices</TableHead>
                  <TableHead className="text-right">Total Purchases</TableHead>
                  <TableHead className="text-right">Total Paid</TableHead>
                  <TableHead className="text-right">Outstanding</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {customerLoading ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-8 text-center text-sm text-gray-500">Loading...</td>
                  </tr>
                ) : !customerData?.length ? (
                  <TableEmpty colSpan={7} message="No customer data available" />
                ) : (
                  customerData.map((row: CustomerReportEntry) => (
                    <TableRow key={row.id}>
                      <TableCell className="font-medium">{row.displayName}</TableCell>
                      <TableCell className="text-gray-500">{row.email || '-'}</TableCell>
                      <TableCell className="text-right">{row.invoiceCount}</TableCell>
                      <TableCell className="text-right">{formatCurrency(row.totalPurchases)}</TableCell>
                      <TableCell className="text-right text-green-600">{formatCurrency(row.totalPaid)}</TableCell>
                      <TableCell className="text-right text-orange-600">{formatCurrency(row.outstanding)}</TableCell>
                      <TableCell>
                        <Badge variant={row.status === 'active' ? 'success' : 'default'}>
                          {row.status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Product Sales Report */}
      {activeReport === 'products' && (
        <Card>
          <CardHeader>
            <CardTitle>Top Selling Products</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product</TableHead>
                  <TableHead>SKU</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead className="text-right">Qty Sold</TableHead>
                  <TableHead className="text-right">Revenue</TableHead>
                  <TableHead className="text-right">Avg Price</TableHead>
                  <TableHead className="text-right">Invoices</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {productLoading ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-8 text-center text-sm text-gray-500">Loading...</td>
                  </tr>
                ) : !productData?.length ? (
                  <TableEmpty colSpan={7} message="No product sales data for this period" />
                ) : (
                  productData.map((row: ProductSalesEntry) => (
                    <TableRow key={row.productId}>
                      <TableCell className="font-medium">{row.productName}</TableCell>
                      <TableCell className="text-gray-500">{row.sku || '-'}</TableCell>
                      <TableCell>{row.categoryName || '-'}</TableCell>
                      <TableCell className="text-right">{row.quantitySold}</TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(row.totalRevenue)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(row.averagePrice)}</TableCell>
                      <TableCell className="text-right">{row.invoiceCount}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Aged Receivables Report */}
      {activeReport === 'receivables' && (
        <Card>
          <CardHeader>
            <CardTitle>Aged Receivables</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Customer</TableHead>
                  <TableHead className="text-right">Current</TableHead>
                  <TableHead className="text-right">1-30 Days</TableHead>
                  <TableHead className="text-right">31-60 Days</TableHead>
                  <TableHead className="text-right">61-90 Days</TableHead>
                  <TableHead className="text-right">90+ Days</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {receivablesLoading ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-8 text-center text-sm text-gray-500">Loading...</td>
                  </tr>
                ) : !receivablesData?.length ? (
                  <TableEmpty colSpan={7} message="No outstanding receivables" />
                ) : (
                  <>
                    {receivablesData.map((row: AgedReceivablesEntry) => (
                      <TableRow key={row.customerId}>
                        <TableCell className="font-medium">{row.customerName}</TableCell>
                        <TableCell className="text-right">{formatCurrency(row.current)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(row.days1to30)}</TableCell>
                        <TableCell className="text-right text-yellow-600">{formatCurrency(row.days31to60)}</TableCell>
                        <TableCell className="text-right text-orange-600">{formatCurrency(row.days61to90)}</TableCell>
                        <TableCell className="text-right text-red-600">{formatCurrency(row.over90)}</TableCell>
                        <TableCell className="text-right font-semibold">{formatCurrency(row.total)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="bg-gray-50 font-semibold">
                      <TableCell>Total</TableCell>
                      <TableCell className="text-right">{formatCurrency(receivablesData.reduce((s, r) => s + r.current, 0))}</TableCell>
                      <TableCell className="text-right">{formatCurrency(receivablesData.reduce((s, r) => s + r.days1to30, 0))}</TableCell>
                      <TableCell className="text-right text-yellow-600">{formatCurrency(receivablesData.reduce((s, r) => s + r.days31to60, 0))}</TableCell>
                      <TableCell className="text-right text-orange-600">{formatCurrency(receivablesData.reduce((s, r) => s + r.days61to90, 0))}</TableCell>
                      <TableCell className="text-right text-red-600">{formatCurrency(receivablesData.reduce((s, r) => s + r.over90, 0))}</TableCell>
                      <TableCell className="text-right">{formatCurrency(receivablesData.reduce((s, r) => s + r.total, 0))}</TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default ReportsPage;
